import getConfig from 'next/config';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { setupCache, buildMemoryStorage, buildKeyGenerator } from 'axios-cache-interceptor';
import hash from 'object-hash';
import logger from '../../logger/server';

export type Variables = { [key: string]: any };
export type RequestOptions = AxiosRequestConfig;

export interface GraphQLRequest<V> {
  query: string,
  variables?: V,
}

export interface GraphQLClient {
  query<T = any, V = Variables>(request: GraphQLRequest<V>): Promise<{ data: T }>,
}

let graphQlClient: GraphQLClient = null;

class GraphQLClientImpl implements GraphQLClient {
  private url: string;

  private options: RequestOptions;

  private instance: AxiosInstance;

  constructor(url: string, options?: RequestOptions) {
    this.url = url;
    this.options = options || {};

    if (process.env.NODE_ENV === 'production') {
      this.instance = setupCache(axios, {
        generateKey: buildKeyGenerator((request) => ({
          url: request.url,
          method: request.method,
          custom: hash({
            url: request.url,
            method: request.method,
            data: request.data,
            headers: request.headers,
          }),
        })),
        methods: ['post'],
        ttl: 1000 * 60 * 15, // 15 minutes
        interpretHeader: false, // disable cache-control
        storage: buildMemoryStorage(),
      });
    }
  }

  async query<T = any, V = Variables>(request: GraphQLRequest<V>): Promise<{ data: T }> {
    // unwrap from axios 'data' and use only graphql 'data'
    return (process.env.NODE_ENV === 'production' ? this.instance : axios).post<{ data: T }>(this.url, request, this.options)
      .then((response: AxiosResponse<{ data: T }>) => response.data)
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          logger.error({ err: error }, 'GraphQL error response:', {
            status: error.response?.status,
            data: error.response?.data ? JSON.stringify(error.response?.data) : undefined,
            headers: error.response?.headers,
          });
        } else {
          logger.error({ err: error }, 'Unexpected error:');
        }
        throw error;
      });
  }
}

function create() {
  const {
    publicRuntimeConfig: {
      GRAPHCMS_URL,
      GRAPHCMS_AUTH_TOKEN,
    },
  } = getConfig();

  return new GraphQLClientImpl(GRAPHCMS_URL, {
    headers: {
      Authorization: GRAPHCMS_AUTH_TOKEN ? `Bearer ${GRAPHCMS_AUTH_TOKEN}` : '',
    },
  });
}

export default function initGraphQLClient() {
  if (!graphQlClient) {
    graphQlClient = create();
  }

  return graphQlClient;
}
